import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, UserActionNotification } from '../services/notifications/notificationsApi';

const LAST_VIEWED_STORAGE_KEY = 'notifications:last-viewed-at';

const sortNotifications = (notifications: UserActionNotification[]) =>
  [...notifications].sort((a, b) => {
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    return bTime - aTime;
  });

export const notificationsQueryKey = (userId?: string | null) => ['notifications', userId] as const;

export const useNotifications = (userId?: string | null) => {
  const queryClient = useQueryClient();
  const [isTrayOpen, setIsTrayOpen] = useState(false);
  const [lastViewedAt, setLastViewedAt] = useState<string>(() => {
    if (typeof window === 'undefined') return new Date(0).toISOString();
    return localStorage.getItem(LAST_VIEWED_STORAGE_KEY) || new Date(0).toISOString();
  });

  const queryKey = notificationsQueryKey(userId);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: notificationsApi.list,
    enabled: !!userId,
  });

  const notifications = useMemo(() => sortNotifications(data || []), [data]);

  const refreshNotifications = useCallback(async () => {
    if (!userId) return;
    await queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey, userId]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    queryClient.setQueryData<UserActionNotification[]>(queryKey, (current) =>
      (current || []).filter((notification) => notification.id !== notificationId));

    try {
      await notificationsApi.remove(notificationId);
    } catch {
      if (userId) {
        void refreshNotifications();
      }
    }
  }, [queryClient, queryKey, refreshNotifications, userId]);

  // Refresh the list whenever the tray is opened so the user always sees fresh data.
  useEffect(() => {
    if (isTrayOpen) {
      void refreshNotifications();
    }
  }, [isTrayOpen, refreshNotifications]);

  useEffect(() => {
    if (!isTrayOpen) return;

    const nextViewedAt = new Date().toISOString();
    setLastViewedAt(nextViewedAt);
    localStorage.setItem(LAST_VIEWED_STORAGE_KEY, nextViewedAt);
  }, [isTrayOpen]);

  const unreadCount = useMemo(() => {
    const viewedAt = new Date(lastViewedAt).getTime();
    return notifications.filter((notification) => {
      const createdAt = new Date(notification.createdAt || 0).getTime();
      return createdAt > viewedAt;
    }).length;
  }, [lastViewedAt, notifications]);

  return {
    notifications,
    unreadCount,
    isTrayOpen,
    isLoading,
    setIsTrayOpen,
    refreshNotifications,
    deleteNotification,
  };
};
